import React, { useState, useRef, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';

const FrequencyGenerator = ({ client }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [selectedFrequency, setSelectedFrequency] = useState(null);
    const [volume, setVolume] = useState(0.3);
    const [duration, setDuration] = useState(0); // seconds elapsed

    // Feedback State
    const [feedback, setFeedback] = useState('');
    const [saving, setSaving] = useState(false);

    const audioContextRef = useRef(null);
    const oscillatorRef = useRef(null);
    const gainNodeRef = useRef(null);
    const timerRef = useRef(null);

    // Frequenze di Solfeggio con descrizioni
    const frequencies = [
        {
            hz: 174,
            name: "Base & Guarigione",
            description: "Guarigione spirituale, fondamenta sicure",
            color: "bg-slate-700",
            icon: "🌱",
            effects: "Riduzione dolore fisico, senso di sicurezza, radicamento"
        },
        {
            hz: 285,
            name: "Riparazione Tessuti",
            description: "Cognizione quantica, rigenerazione cellulare",
            color: "bg-slate-600",
            icon: "🔬",
            effects: "Guarigione ferite, rigenerazione, energia vitale"
        },
        {
            hz: 396,
            name: "Liberazione",
            description: "Liberazione da sensi di colpa, ansia e paure",
            color: "bg-slate-700",
            icon: "🦋",
            effects: "Riduzione ansia, rilascio blocchi emotivi, liberazione traumi"
        },
        {
            hz: 417,
            name: "Cambiamento",
            description: "Superamento traumi, rinnovamento",
            color: "bg-slate-600",
            icon: "🔄",
            effects: "Facilitazione cambiamento, energia nuova, pattern breaking"
        },
        {
            hz: 528,
            name: "Miracolo",
            description: 'Armonizzazione DNA, rigenerazione cellulare',
            color: "bg-slate-700",
            icon: "✨",
            effects: "Trasformazione profonda, riparazione DNA, armonia interiore"
        },
        {
            hz: 639,
            name: "Relazioni",
            description: "Apertura relazionale, risoluzione tensioni",
            color: "bg-slate-600",
            icon: "💙",
            effects: "Comunicazione autentica, empatia, connessione con gli altri"
        },
        {
            hz: 741,
            name: "Risveglio",
            description: "Risveglio interiore, pulizia, creatività",
            color: "bg-slate-700",
            icon: "🎨",
            effects: "Espressione creativa, intuizione, chiarezza mentale"
        },
        {
            hz: 852,
            name: "Ordine Spirituale",
            description: "Amore incondizionato, sostituzione pensieri negativi",
            color: "bg-slate-600",
            icon: "💜",
            effects: "Elevazione pensieri, connessione spirituale, pace interiore"
        },
        {
            hz: 963,
            name: "Unità",
            description: "Ritorno all'Uno, attivazione ghiandola pineale",
            color: "bg-slate-700",
            icon: "🌟",
            effects: "Coscienza espansa, illuminazione, connessione universale"
        }
    ];

    // Initialize Web Audio API
    const initAudio = () => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            gainNodeRef.current = audioContextRef.current.createGain();
            gainNodeRef.current.connect(audioContextRef.current.destination);
            gainNodeRef.current.gain.value = volume;
        }
    };

    // Play frequency
    const playFrequency = (freq) => {
        initAudio();

        // Stop any currently playing sound
        stopFrequency();

        // Create oscillator
        const oscillator = audioContextRef.current.createOscillator();
        oscillator.type = 'sine'; // Pure sine wave
        oscillator.frequency.setValueAtTime(freq.hz, audioContextRef.current.currentTime);
        oscillator.connect(gainNodeRef.current);
        oscillator.start();

        oscillatorRef.current = oscillator;
        setIsPlaying(true);
        setSelectedFrequency(freq);
        setDuration(0);

        // Start timer
        timerRef.current = setInterval(() => {
            setDuration(prev => prev + 1);
        }, 1000);
    };

    // Stop frequency
    const stopFrequency = () => {
        if (oscillatorRef.current) {
            oscillatorRef.current.stop();
            oscillatorRef.current.disconnect();
            oscillatorRef.current = null;
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setIsPlaying(false);
        setDuration(0);
    };

    // Update volume
    useEffect(() => {
        if (gainNodeRef.current) {
            gainNodeRef.current.gain.value = volume;
        }
    }, [volume]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopFrequency();
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, []);

    // Format duration as MM:SS
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleSaveFeedback = async () => {
        if (!selectedFrequency || !feedback.trim()) return;

        try {
            setSaving(true);
            await DataManager.saveWork({
                clientId: client.id,
                type: 'frequency_feedback',
                content: feedback,
                frequencyHz: selectedFrequency.hz,
                frequencyName: selectedFrequency.name,
                shared: true, // Visible to counselor (and self)
                timestamp: new Date().toISOString()
            });
            setFeedback('');
            alert('Feedback salvato correttamente in "Esperienze"!');
        } catch (error) {
            console.error('Error saving feedback:', error);
            alert('Errore nel salvataggio del feedback');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6">
                <div className="flex items-start gap-4">
                    <span className="text-5xl grayscale">🎵</span>
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800 mb-2 font-sans">Generatore Frequenze Solfeggio</h2>
                        <p className="text-slate-600 mb-4 font-sans">
                            Le frequenze di Solfeggio sono toni puri utilizzati per il riequilibrio energetico,
                            la meditazione e il rilassamento profondo. Ogni frequenza agisce su aspetti specifici
                            del benessere psico-fisico.
                        </p>
                        <div className="bg-slate-100 border-l-4 border-slate-800 p-4 rounded">
                            <p className="text-sm text-slate-800 font-sans">
                                <strong>💡 Come usare:</strong><br />
                                1. Scegli la frequenza in base all'obiettivo della sessione<br />
                                2. Crea un ambiente tranquillo (luci soffuse, assenza distrazioni)<br />
                                3. Invita il cliente a chiudere gli occhi e respirare profondamente<br />
                                4. Lascia suonare per 5-15 minuti durante meditazione o visualizzazione<br />
                                5. Usa cuffie per migliore efficacia
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Player Control (if playing) */}
            {isPlaying && selectedFrequency && (
                <div className="bg-slate-800 rounded-3xl shadow-xl p-8 text-white">
                    <div className="text-center mb-6">
                        <div className="text-6xl mb-4 grayscale">{selectedFrequency.icon}</div>
                        <h3 className="text-3xl font-bold mb-2 font-sans">{selectedFrequency.hz} Hz</h3>
                        <p className="text-xl opacity-90 font-sans">{selectedFrequency.name}</p>
                    </div>

                    <div className="bg-slate-700 rounded-2xl p-6 mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-lg font-medium font-sans">Durata:</span>
                            <span className="text-3xl font-mono font-bold">{formatDuration(duration)}</span>
                        </div>

                        {/* Volume Control */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium font-sans">Volume: {Math.round(volume * 100)}%</label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={volume}
                                onChange={(e) => setVolume(parseFloat(e.target.value))}
                                className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>
                    </div>

                    <button
                        onClick={stopFrequency}
                        className="w-full py-4 bg-white text-slate-900 rounded-2xl font-bold text-lg hover:bg-slate-100 transition shadow-xl font-sans border-0"
                    >
                        ⏸️ Ferma Frequenza
                    </button>

                    {/* Feedback Box In-Player */}
                    <div className="mt-8 bg-slate-700 rounded-2xl p-4">
                        <label className="block text-sm font-medium mb-2 font-sans">💭 Feedback Esperienziale (Cosa senti?)</label>
                        <textarea
                            className="w-full p-3 rounded-xl bg-slate-600 text-white placeholder-slate-400 border border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400 font-sans"
                            rows="2"
                            placeholder="Sensazioni fisiche, emozioni, immagini..."
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                        />
                        <button
                            onClick={handleSaveFeedback}
                            disabled={!feedback.trim() || saving}
                            className={`mt-2 w-full py-2 rounded-xl text-sm font-bold transition flex items-center justify-center gap-2 font-sans border-0 ${!feedback.trim() ? 'bg-slate-600 text-slate-400 cursor-not-allowed' : 'bg-white text-slate-900 hover:bg-slate-100'
                                }`}
                        >
                            {saving ? 'Salvataggio...' : '💾 Salva Esperienza'}
                        </button>
                    </div>
                </div>
            )}

            {/* Frequency Grid */}
            <div className="grid md:grid-cols-3 gap-4">
                {frequencies.map((freq) => (
                    <div
                        key={freq.hz}
                        className={`${freq.color} rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300 cursor-pointer overflow-hidden border-2 ${selectedFrequency?.hz === freq.hz && isPlaying ? 'border-slate-900' : 'border-transparent'
                            }`}
                        onClick={() => !isPlaying && playFrequency(freq)}
                    >
                        <div className="p-6 text-white">
                            <div className="text-5xl mb-3 grayscale">{freq.icon}</div>
                            <h3 className="text-2xl font-bold mb-2 font-sans">{freq.hz} Hz</h3>
                            <p className="text-lg font-semibold mb-2 opacity-90 font-sans">{freq.name}</p>
                            <p className="text-sm opacity-80 mb-4 font-sans">{freq.description}</p>

                            <div className="bg-white/20 rounded-xl p-3">
                                <p className="text-xs font-medium font-sans">
                                    {freq.effects}
                                </p>
                            </div>

                            {!isPlaying && (
                                <button className="w-full mt-4 py-3 bg-white/30 hover:bg-white/40 rounded-xl font-bold text-sm transition font-sans border-0">
                                    ▶️ Riproduci
                                </button>
                            )}

                            {selectedFrequency?.hz === freq.hz && isPlaying && (
                                <div className="mt-4 text-center">
                                    <span className="inline-block px-4 py-2 bg-white/30 rounded-full text-sm font-bold font-sans">
                                        🎵 In Riproduzione
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Scientific Background */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <h4 className="font-bold text-slate-800 mb-4 text-xl font-sans">📚 Fondamenti Scientifici</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-700 font-sans">
                    <div className="bg-white p-4 rounded-xl border border-slate-200">
                        <strong className="block mb-2 text-slate-900">🧬 Risonanza Cellulare:</strong>
                        Le frequenze di Solfeggio interagiscono con le frequenze naturali delle cellule,
                        favorendo processi di auto-guarigione e riequilibrio energetico.
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-slate-200">
                        <strong className="block mb-2 text-slate-900">🧠 Onde Cerebrali:</strong>
                        I toni puri inducono stati di coscienza rilassati (Alpha/Theta),
                        facilitando meditazione profonda e accesso all'inconscio.
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-slate-200">
                        <strong className="block mb-2 text-slate-900">❤️‍🩹 Stress Reduction:</strong>
                        Studi dimostrano riduzione cortisolo, aumento ossitocina e miglioramento
                        variabilità cardiaca (HRV) durante ascolto prolungato.
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-slate-200">
                        <strong className="block mb-2 text-slate-900">🎭 Uso Terapeutico:</strong>
                        Integrabile in musicoterapia, EMDR, visualizzazioni guidate,
                        body scan e pratiche mindfulness per amplificare efficacia.
                    </div>
                </div>
            </div>

            {/* Safety Note */}
            <div className="bg-slate-50 border-2 border-slate-300 rounded-2xl p-4">
                <h5 className="font-bold text-slate-900 mb-2 flex items-center gap-2 font-sans">
                    ⚠️ Note Importanti
                </h5>
                <ul className="text-sm text-slate-700 space-y-1 font-sans">
                    <li>• Non sostituisce trattamenti medici o farmacologici</li>
                    <li>• Volume moderato per evitare affaticamento uditivo</li>
                    <li>• Sconsigliato in caso di epilessia fotosensibile o disturbi dell'udito gravi</li>
                    <li>• Usare con cautela in gravidanza (consultare professionista)</li>
                    <li>• Ottimale in sessioni 10-20 minuti, max 2-3 volte al giorno</li>
                </ul>
            </div>
        </div>
    );
};

export default FrequencyGenerator;
